import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "./ToastProvider";
import cheapestIcon from "../assets/cheapest.svg";
import discountIcon from "../assets/discount.svg";
import saveIcon from "../assets/saveIcon.svg";

function formatRelativeUpdated(product) {
  const scrapeTime = product?.scrape_time;
  if (!scrapeTime) return null;
  const now = new Date();
  const today = now.toISOString().split("T")[0];
  const scrapeDate = new Date(`${today}T${scrapeTime}`);
  if (isNaN(scrapeDate.getTime())) return null;
  let diffSec = Math.floor((now - scrapeDate) / 1000);
  if (diffSec < 0) diffSec += 24 * 60 * 60;
  if (diffSec < 45) return "Updated just now";
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `Updated ${min} min ago`;

  const hr = Math.floor(min / 60);
  if (hr < 24) return `Updated ${hr} hr ago`;

  const day = Math.floor(hr / 24);
  return `Updated ${day} day${day === 1 ? "" : "s"} ago`;
}

function ProductCard({
  accent,
  product,
  onClick,
  onAddToList,
  retailerLabel,
  showComparison = true,
}) {
  void accent;
  const [addBusy, setAddBusy] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();

  const normalizePlatformKey = (platform) => {
    const p = String(platform || "").trim().toLowerCase();
    if (p === "morrisons") return "morrison";
    if (p === "sainsburry") return "sainsbury";
    if (p === "sainsburys") return "sainsbury";
    return p;
  };

  const parsePrice = (value) => {
    if (value == null) return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    const normalized = String(value).trim().replace(",", ".").replace(/[^0-9.-]/g, "");
    if (!normalized) return null;
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const platformMeta = {
    tesco: { label: "Tesco", priceColor: "#5B3CC4" },
    asda: { label: "ASDA", priceColor: "#5B3CC4" },
    morrison: { label: "Morrisons", priceColor: "#5B3CC4" },
    sainsbury: { label: "Sainsbury's", priceColor: "#5B3CC4" },
  };

  const knownPlatformKey = normalizePlatformKey(product?.platform);
  const knownStoreMeta = platformMeta[knownPlatformKey] || null;

  const retailerLabelToPlatformKey = (label) => {
    const l = String(label || "").trim().toLowerCase();
    if (l.includes("tesco")) return "tesco";
    if (l.includes("morris")) return "morrison";
    if (l.includes("asda")) return "asda";
    if (l.includes("sainsbury")) return "sainsbury";
    return "";
  };

  const effectivePlatformKey = retailerLabel
    ? retailerLabelToPlatformKey(retailerLabel)
    : knownPlatformKey;

  const comparisonSource =
    Array.isArray(product?.comparison) && product.comparison.length > 0
      ? product.comparison
      : ["tesco", "morrison", "asda", "sainsbury"].map((platform) => ({
          platform,
          price: platform === effectivePlatformKey ? product?.price : null,
        }));

  const comparisonRows = comparisonSource.map((item) => {
    const platform = normalizePlatformKey(
      item?.platform || item?.retailer || item?.provider || item?.store || item?.site
    );
    const meta = platformMeta[platform] || {
      label: item?.platform || item?.retailer || item?.provider || item?.store || "Store",
      priceColor: "#5B3CC4",
    };
    const numericPrice = parsePrice(
      item?.price ?? item?.unit_price ?? item?.price_per_unit ?? item?.offer_price
    );
    return {
      platform,
      store: meta.label,
      price: Number.isFinite(numericPrice) ? numericPrice : null,
      formatted: Number.isFinite(numericPrice) ? `£${numericPrice.toFixed(2)}` : "N/A",
      priceColor: meta.priceColor,
    };
  });

  const knownPrices = comparisonRows
    .map((r) => r.price)
    .filter((p) => typeof p === "number" && Number.isFinite(p));

  const cheapestPrice = knownPrices.length ? Math.min(...knownPrices) : null;
  const cheapestRow =
    cheapestPrice == null ? null : comparisonRows.find((r) => r.price === cheapestPrice) || null;

  const highestPrice = knownPrices.length ? Math.max(...knownPrices) : null;
  const potentialSavings =
    cheapestPrice != null && highestPrice != null ? highestPrice - cheapestPrice : null;
  const savings =
    typeof potentialSavings === "number" &&
    Number.isFinite(potentialSavings) &&
    potentialSavings > 0
      ? potentialSavings
      : null;

  const updatedLabel = formatRelativeUpdated(product);
  const categoryLabel = product?.category ? String(product.category) : null;
  const sizeLabel = product?.packsize || product?.weight || null;

  const effectiveRow = comparisonRows.find((r) => r.platform === effectivePlatformKey);
  const displayPriceFormatted =
    effectiveRow?.formatted || (parsePrice(product?.price) != null
      ? `£${parsePrice(product?.price).toFixed(2)}`
      : "£—");

  const promoText = String(product?.promotion || "").trim();
  const showPromoBanner = !showComparison && promoText.length > 0;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick?.();
      }}
      className="flex h-full min-h-0 flex-col rounded-[18px] border border-[#E6EAF0] bg-white text-left shadow-sm transition-shadow hover:shadow-md focus:outline-none"
    >
      <div className="flex min-h-0 flex-1 flex-col p-4">
        {/* Image */}
        <div className="mb-3 flex h-[200px] items-center justify-center rounded-xl bg-[#FFFFFF]">
          {product?.images?.[0] ? (
            <img
              src={product.images[0]}
              alt={product.title || product.sku}
              className="h-full w-full object-contain"
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-xl border border-[#E6EAF0] bg-slate-50">
              <svg
                width="34"
                height="34"
                viewBox="0 0 24 24"
                fill="none"
                className="text-slate-400"
                aria-hidden
              >
                <path
                  d="M21 16.2V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v11.2"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
                <path
                  d="M3.5 18.5l4.7-4.7a1.5 1.5 0 0 1 2.1 0l2.4 2.4a1.5 1.5 0 0 0 2.1 0l1.2-1.2a1.5 1.5 0 0 1 2.1 0L21 18.2"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.5 8.5h.01"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
              <div className="text-center">
                <div className="text-[13px] font-semibold text-slate-600">Image unavailable</div>
                <div className="text-[11px] font-medium text-slate-400">We’ll update it soon</div>
              </div>
            </div>
          )}
        </div>

        <p className="text-[18px] font-semibold text-[#24303A] line-clamp-2 min-h-[40px]">
          {product?.title}
        </p>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          <div className="flex flex-wrap items-center gap-2">
            {categoryLabel && (
              <span className="inline-flex rounded-full bg-[#2EB5C9] px-2.5 py-0.5 text-[12px] font-semibold text-[#FFFFFF]">
                {categoryLabel}
              </span>
            )}
            {sizeLabel && (
              <span className="inline-flex rounded-full bg-[#F59E0B] px-2.5 py-0.5 text-[12px] font-semibold text-[#FFFFFF]">
                {String(sizeLabel)}
              </span>
            )}
          </div>
          {updatedLabel && (
            <span className="shrink-0 text-[12px] font-normal text-[#6E7B86]">{updatedLabel}</span>
          )}
        </div>

        {showComparison ? (
          <>
            <div className="mt-3 rounded-xl border-2 border-[#2FC98D80] bg-[#2FC98D1A] px-3 py-2.5">
              <div className="flex items-start gap-2">
                <img
                  src={cheapestIcon}
                  alt=""
                  width={18}
                  height={18}
                  className="mt-0.5 h-[18px] w-[18px] shrink-0 object-contain"
                  aria-hidden
                />
                <p className="text-[14px] font-semibold leading-snug text-[#24303A]">
                  Cheapest at {cheapestRow?.store || "—"}:{" "}
                  <span className="font-semibold text-[#24303A]">
                    {cheapestRow?.formatted || "N/A"}
                  </span>
                </p>
              </div>
              {savings != null ? (
                <div className="mt-1 flex items-center gap-1.5 text-[11px] font-semibold text-rose-600">
                  <img
                    src={saveIcon}
                    alt=""
                    width={18}
                    height={18}
                    className="h-[18px] w-[18px] shrink-0 object-contain"
                    aria-hidden
                  />
                  <span className="text-[#E44A77] font-semibold text-[14px]">
                    Save £{savings.toFixed(2)} vs highest price
                  </span>
                </div>
              ) : (
                <div className="mt-2 h-[18px]" aria-hidden />
              )}
            </div>

            <div className="mt-2 space-y-1.5 text-[14px]">
              {comparisonRows.map((row) => {
                const isBest =
                  cheapestRow != null && row.platform === cheapestRow.platform && row.price != null;
                return (
                  <div key={row.platform} className="flex items-center justify-between gap-3">
                    <span
                      className={"text-[#24303A] font-normal text-[14px]"
                      }
                    >
                      {row.store}
                      {isBest ? " (Best)" : ""}
                    </span>
                    <span
                      className={`font-semibold ${isBest ? "text-[#2FC98D]" : "text-[#7352C7]"}`}
                    >
                      {row.formatted}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            {showPromoBanner && (
              <div className="mt-3 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/90 px-3 py-1.5">
                <img
                  src={discountIcon}
                  alt=""
                  width={18}
                  height={18}
                  className="h-[18px] w-[18px] shrink-0 object-contain"
                  aria-hidden
                />
                <p className="text-[13px] font-semibold text-[#2FC98D]">
                  {promoText}
                </p>
              </div>
            )}

            <div className="mt-3 flex items-center justify-between gap-3 text-[13px]">
              <span className="font-normal text-[#24303A] text-[14px]">
                {retailerLabel || knownStoreMeta?.label || "Store"}
              </span>
              <span className="text-[13px] font-semibold text-[#7352C7]">
                {displayPriceFormatted}
              </span>
            </div>
          </>
        )}

        <div className="mt-auto grid grid-cols-2 gap-2 pt-4">
          <button
            type="button"
            className="rounded-full bg-[#7352C71A] px-3 py-1.5 text-[14px] font-bold text-[#7352C7] shadow-sm"
            onClick={(e) => {
              e.stopPropagation();
              onClick?.();
            }}
          >
            View details
          </button>
          <button
            type="button"
            disabled={addBusy}
            className="rounded-full bg-[#7352C7] px-3 py-1.5 text-[14px] font-bold text-white shadow-sm "
            onClick={async (e) => {
              e.stopPropagation();
              if (typeof onAddToList !== "function" || addBusy) return;
              setAddBusy(true);
              try {
                await onAddToList(product);
                showToast({
                  type: "success",
                  title: "Added to list",
                  message: "Product added to grocery list.",
                  actionLabel: "Go to Grocery List",
                  onAction: () => {
                    window.location.assign("/settings?tab=grocery&view=lists");
                  },
                });
              } catch (err) {
                const errorMessage = err?.message || "Failed to add product to list.";
                if (errorMessage === "ADD_TO_LIST_CANCELLED" || err?.code === "ADD_TO_LIST_CANCELLED") {
                  return;
                }
                const isNoListError = /No grocery list found/i.test(errorMessage);
                showToast({
                  type: "error",
                  title: "Add failed",
                  message: errorMessage,
                  actionLabel: isNoListError ? "Create list" : "",
                  onAction: isNoListError
                    ? () => navigate("/settings?tab=grocery&view=create")
                    : null,
                });
              } finally {
                setAddBusy(false);
              }
            }}
          >
            {addBusy ? "Adding..." : "Add to list"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductCard;
